import type { Article, GuidePagination } from './types'

import api, { isCancel } from '@/utils/api'
import { DEFAULT_PAGINATION } from './types'

export type ArticleQueryParams = {
  /** '' | 'asc' | 'desc' — empty means the default order. */
  sort: string
  search: string
  tagSlugs: string[]
  page: number
  /** Restricts to one guide category; omitted on the all-guides listing. */
  categorySlug?: string
}

/**
 * Build the Strapi query string for the articles listing.
 *
 * Split out from `fetchArticles` purely so the bracket encoding — which Strapi
 * is picky about — can be unit-tested without a network call.
 */
export function buildArticleQuery(params: ArticleQueryParams): string {
  const { sort, search, tagSlugs, page, categorySlug } = params
  const query = new URLSearchParams()

  query.append('sort', sort ? `date:${sort}` : 'order:asc,date:desc')
  if (categorySlug) {
    query.append('filters[guide_category][slug][$eq]', categorySlug)
  }
  if (search) {
    query.append('filters[title][$containsi]', search)
  }
  if (page) {
    query.append('pagination[page]', String(page))
    query.append('pagination[pageSize]', String(DEFAULT_PAGINATION.pageSize))
  }
  if (tagSlugs?.length) {
    tagSlugs.forEach(slug => {
      query.append('filters[article_tags][slug][$eq]', slug)
    })
  }

  return query.toString()
}

/**
 * Fetch one page of articles. Returns `null` when the request was aborted, so
 * callers can distinguish "superseded by a newer request" from "empty result".
 */
export async function fetchArticles(
  params: ArticleQueryParams & { controller?: AbortController }
): Promise<{ articles: Article[]; pagination?: GuidePagination } | null> {
  const { controller, ...queryParams } = params

  try {
    const response = await api.get(`/articles?pLevel`, {
      params: new URLSearchParams(buildArticleQuery(queryParams)),
      signal: controller?.signal
    })

    // `meta.pagination` is present at runtime but not in the OpenAPI spec.
    const meta = (response.data as { meta?: { pagination?: GuidePagination } }).meta

    return { articles: response.data.data, pagination: meta?.pagination }
  } catch (error) {
    if (isCancel(error)) {
      console.log('Request canceled', (error as { message?: string }).message)
    } else {
      console.error('Error fetching data', error)
    }
    return null
  }
}
